import React, { useEffect, useRef, useState } from "react";
import { StyledSelectListForm } from "./style";
import { DropDownListOptionType } from "types/common/CommonType";
import { Icon } from "components/icon/Icon";
import { Checkbox } from "components/button/Checkbox/Checkbox";
import { Util } from "Util";

/**
 * 드롭다운 리스트 폼 컴포넌트 속성
 */
interface SelectListFormProps {
  size: "large" | "medium" | "small";
  option: DropDownListOptionType;
  value?: DropDownListOptionType[] | DropDownListOptionType;
  isFocused?: boolean;
  showIndent?: boolean;
  searchValue?: string;
  forceCheck?: boolean;
  enableFontSelection?: boolean;
  handleValue?: (value: DropDownListOptionType) => void;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * SelectListForm component
 * @author hc_yang
 * @param {SelectListFormProps} param
 * @returns JSX.Element
 */
export const SelectListForm = ({
  size,
  option,
  value,
  isFocused = false,
  showIndent = false,
  searchValue,
  forceCheck,
  enableFontSelection = false,
  handleValue,
  onClick,
}: SelectListFormProps) => {
  const contentRef = useRef<HTMLParagraphElement | null>(null);
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [label, setLabel] = useState<string>(option.label);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isTooltip, setIsTooltip] = useState<boolean>(false);

  const isMultiple = Array.isArray(value);
  const isSelected =
    (isMultiple
      ? value?.findIndex((item) => item.value === option.value) !== -1
      : value?.value === option.value) || false;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) onClick(e);
    handleValue && handleValue(option);
  };

  const checkContentOverflow = () => {
    if (contentRef.current) {
      return contentRef.current.scrollWidth > contentRef.current.clientWidth;
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsHovered(true);
    if (checkContentOverflow()) setIsTooltip(true);
  };
  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsHovered(false);
    if (isTooltip) setIsTooltip(false);
  };

  useEffect(() => {
    if (searchValue) {
      const regex = new RegExp(Util.escapeRegExp(searchValue), "gi");
      setLabel(
        option.label.replace(
          regex,
          `<span style="background-color: var(--sec-orange-3);">$&</span>`
        )
      );
    } else {
      setLabel(option.label);
    }
  }, [searchValue]);

  return (
    <StyledSelectListForm
      ref={parentRef}
      $size={size}
      $focused={isFocused}
      $disabled={option.disabled || false}
      $allSelection={option.allSelection || false}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showIndent && <span className="indent-box" />}
      {option.icon && (
        <Icon
          icon={option.icon}
          size={size === "large" ? 20 : size === "medium" ? 16 : 12}
          stroke="--text-gray-strong"
        />
      )}
      <p
        ref={contentRef}
        className="label"
        dangerouslySetInnerHTML={{ __html: label }}
        style={
          (enableFontSelection && {
            fontFamily: `'${option.value}', serif`,
            fontWeight: 400,
          }) ||
          undefined
        }
      />
      {(isMultiple || isSelected) && (
        <Checkbox
          disabled={option.disabled}
          size={size === "large" ? "large" : "small"}
          isHovered={!option.disabled && (isFocused || isHovered)}
          checked={forceCheck || isSelected}
        />
      )}
    </StyledSelectListForm>
  );
};
